import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { ConfigEstoqueService } from '../../../../shared/services/apis/suprimentos/config-estoque.service';
import { ConfigEstoqueRequest } from '../../../../shared/services/requests/suprimentos/config-estoque.request';
import { ConfigEstoqueResponse } from '../../../../shared/services/responses/suprimentos/config-estoque.response';

@Component({
  selector: 'app-config-estoque',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
  ],
  templateUrl: './config-estoque.component.html',
  styleUrls: ['./config-estoque.component.css'],
})
export class ConfigEstoqueComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Suprimentos;
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;
  private idConfiguracaoEstoque = 0;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private alertService: AlertService,
    private configEstoqueService: ConfigEstoqueService,
  ) {
    this.form = this.formBuilder.group({
      isEnviarEmailProdutosAbaixoEstoqueMinimo: [false],
      emailsDestinatariosAvisos: [null],
      isEditarQuantidadeImportacaoInventario: [false],
    });
  }

  public ngOnInit(): void {
    this.setupListeners();
    this.loadConfig();
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/suprimentos']);
  }

  public salvar(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios corretamente.', AlertType.error);
      return;
    }

    const request: ConfigEstoqueRequest = {
      isEnviarEmailProdutosAbaixoEstoqueMinimo: this.form.get('isEnviarEmailProdutosAbaixoEstoqueMinimo')?.value ?? false,
      isEditarQuantidadeImportacaoInventario: this.form.get('isEditarQuantidadeImportacaoInventario')?.value ?? false,
      emailsDestinatariosAvisos: this.normalizeEmails(this.form.get('emailsDestinatariosAvisos')?.value),
    };

    this.isSaving = true;

    const save$ = this.idConfiguracaoEstoque > 0
      ? this.configEstoqueService.put(this.idConfiguracaoEstoque, request)
      : this.configEstoqueService.post(request);

    save$.subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Configurações salvas com sucesso!', AlertType.success);
        this.voltar();
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  private loadConfig(): void {
    this.isLoading = true;

    this.configEstoqueService.get().subscribe({
      next: (response: ConfigEstoqueResponse) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        const configuracao = response.configuracaoEstoque;
        if (configuracao) {
          this.idConfiguracaoEstoque = configuracao.idConfiguracaoEstoque ?? 0;
          this.form.patchValue({
            isEnviarEmailProdutosAbaixoEstoqueMinimo: configuracao.isEnviarEmailProdutosAbaixoEstoqueMinimo,
            emailsDestinatariosAvisos: configuracao.emailsDestinatariosAvisos,
            isEditarQuantidadeImportacaoInventario: configuracao.isEditarQuantidadeImportacaoInventario,
          });
        }

        this.syncEmailValidators(this.form.get('isEnviarEmailProdutosAbaixoEstoqueMinimo')?.value ?? false);
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  private setupListeners(): void {
    this.form.get('isEnviarEmailProdutosAbaixoEstoqueMinimo')?.valueChanges.subscribe(value => {
      this.syncEmailValidators(!!value);
    });
  }

  private syncEmailValidators(isEnabled: boolean): void {
    const emailControl = this.form.get('emailsDestinatariosAvisos');
    if (!emailControl) {
      return;
    }

    if (isEnabled) {
      emailControl.setValidators([Validators.required, this.emailListValidator()]);
    } else {
      emailControl.clearValidators();
    }

    emailControl.updateValueAndValidity({ emitEvent: false });
  }

  private emailListValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = `${control.value ?? ''}`.trim();
      if (!value) {
        return null;
      }

      const emails = value
        .split(';')
        .map(email => email.trim())
        .filter(email => email.length > 0);

      if (emails.length === 0) {
        return { emailListInvalid: true };
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hasInvalidEmail = emails.some(email => !emailRegex.test(email));

      return hasInvalidEmail ? { emailListInvalid: true } : null;
    };
  }

  private normalizeEmails(value: string | null): string | null {
    if (!value?.trim()) {
      return null;
    }

    return value
      .split(';')
      .map(email => email.trim())
      .filter(email => email.length > 0)
      .join('; ');
  }
}